import { Checkbox, Input, Select, Tabs } from '@seiue/ui'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'

import { FieldTypeEnum } from 'packages/sdks-next/growp'

import { SelectProductSetting } from './SelectProductSetting'
import { QuestionnaireContext } from './context'
import { ReqColumnWithMetaData } from './types'
import {
  BaseOptions,
  baseConfigQenerator,
  changeQuestionnaireData,
  generatorMetaData,
  typeToLabel,
} from './utils'

/**
 * 复合类型题目设置
 *
 * @returns React.ReactNode
 */
export const ProductSetting: React.FC = () => {
  const { questionnaires, setQuestionnaires, activeItemIndex } =
    useContext(QuestionnaireContext)
  if (!questionnaires || activeItemIndex === undefined) return null
  const currentQuestion = questionnaires[activeItemIndex]
  const currentChildren = currentQuestion.children || []
  const [activeKey, setActiveKey] = useState<string>('0')

  const onChangeChildData = (index: number, param: object) => {
    if (currentQuestion.children) {
      const currentItem = currentQuestion.children[index]
      const newItem = {
        ...currentItem,
        ...param,
      }
      currentQuestion.children.splice(index, 1, newItem)
      const newQuestionnaire = changeQuestionnaireData(
        questionnaires,
        activeItemIndex,
        {
          ...currentQuestion,
          children: currentQuestion.children,
        }
      )
      setQuestionnaires([...newQuestionnaire])
    }
  }

  const typeOptions = typeToLabel
    .listOptions('value', 'label')
    .filter(
      (option) =>
        ![
          FieldTypeEnum.Product,
          FieldTypeEnum.PageBreak,
          FieldTypeEnum.Describe,
          FieldTypeEnum.Date,
        ].includes(option.value as FieldTypeEnum)
    )

  const tabLen = currentChildren.length
  return (
    <Wrapper>
      <Tabs
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        hideAdd={currentQuestion.isUserField}
        type="editable-card"
        onEdit={(key, action) => {
          if (action === 'remove') {
            currentChildren.splice(Number(key), 1)
            setQuestionnaires([...questionnaires])
            setActiveKey(`${tabLen - 2}`)
          } else {
            currentChildren.splice(currentChildren.length, 0, {
              ...baseConfigQenerator(FieldTypeEnum.String),
              label: typeToLabel.get(FieldTypeEnum.String),
            })
            const newQuestionnaires = changeQuestionnaireData(
              questionnaires,
              activeItemIndex,
              {
                ...currentQuestion,
                children: currentChildren,
              }
            )
            setQuestionnaires([...newQuestionnaires])
            setActiveKey(`${tabLen}`)
          }
        }}
      >
        {currentChildren.map((child, index) => {
          return (
            <Tabs.TabPane
              key={index}
              tab={index + 1}
              closeIcon={
                currentQuestion.isUserField || currentChildren.length < 2 ? (
                  <div />
                ) : null
              }
            >
              <BaseProductWrapper>
                <div>
                  <Title>
                    {child.require ? <RequiredStar>*</RequiredStar> : null}
                    <span>题目标题</span>
                  </Title>
                  <Input
                    disabled={currentQuestion.isUserField}
                    value={child.label}
                    onChange={(e) => {
                      onChangeChildData(index, { label: e.target.value })
                    }}
                    maxLength={200}
                  />
                </div>
                <div>
                  <Title>题目类型</Title>
                  <Select
                    style={{
                      borderRadius: '8px',
                    }}
                    options={typeOptions}
                    disabled={currentQuestion.isUserField}
                    value={child.type}
                    onChange={(v) => {
                      currentChildren.splice(index, 1, {
                        ...child,
                        label: child.label || typeToLabel.get(v),
                        type: v,
                        options: [
                          FieldTypeEnum.Single,
                          FieldTypeEnum.Multi,
                        ].includes(v)
                          ? BaseOptions
                          : undefined,
                        metadata: generatorMetaData(v),
                      })
                      setQuestionnaires([...questionnaires])
                    }}
                    allowClear={false}
                  />
                </div>
                <div>
                  <Checkbox
                    checked={child.require}
                    onChange={(e) =>
                      onChangeChildData(index, { require: e.target.checked })
                    }
                  >
                    必填
                  </Checkbox>
                </div>
              </BaseProductWrapper>
              <SelectProductSetting
                currentChild={child as ReqColumnWithMetaData}
                childIndex={index}
              />
            </Tabs.TabPane>
          )
        })}
      </Tabs>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  &&& {
    .ant-tabs-nav-operations {
      height: 32px;
      & button {
        line-height: 14px;
      }
    }
    .ant-tabs-tab {
      min-width: 54px;
      height: 32px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      background-color: ${(props) => props.theme.background._3};
    }
    .ant-tabs-tab-active {
      background-color: transparent;
    }
    .ant-tabs-nav-add {
      min-width: 54px;
      height: 32px;
      padding: 0 0;
      border: 1px solid transparent;
      background-color: transparent;
    }
    .ant-tabs-tab-active .ant-tabs-tab-btn,
    .ant-tabs-tab-btn {
      font-weight: 400;
      font-size: 14px;
      color: ${(props) => props.theme.brand._1};
    }
    .ant-tabs-tab-btn {
      color: ${(props) => props.theme.text._2};
    }
    .ant-tabs-tabpane {
      & div:last-child {
        box-shadow: none;
        border-bottom: none;
      }
    }
  }
`

const BaseProductWrapper = styled.div`
  padding: 16px 8px;
  border-bottom: 1px solid ${(props) => props.theme.border.greyNormal};
  & > div {
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  &&& {
    .ant-input,
    .ant-input-focused {
      border: 1px solid transparent !important;
      border-radius: 8px;
      background-color: ${(props) => props.theme.background._3};
    }
    .ant-input:focus {
      border-color: ${(props) => props.theme.brand._1} !important;
    }
    .ant-select {
      width: 100%;
      height: 32px;
    }
    /* .ant-select-disabled .ant-select-selector,
    .ant-select-selector,
    .ant-select-focused .ant-select-selector-focused {
      width: 264px;
      border: 1px solid transparent !important;
      border-radius: 8px;
      background-color: ${(props) => props.theme.background._3} !important;
    } */
    .ant-checkbox + span {
      color: ${(props) => props.theme.text._2};
    }
  }
`

const Title = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._2};
`
const RequiredStar = styled.div`
  display: inline-block;
  width: 12px;
  height: 22px;
  color: #f54545;
`
